import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import type { FC } from 'react';

const CategoriesSection: FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<typeof categories[0] | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="categories" className="py-16 md:py-24 bg-background-100">
      <div className="section-container">
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
            <i className="ri-price-tag-3-line text-sm"></i>
            <span>Kategorie</span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            Kategorie Produktów Kakobuy Spreadsheet
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            Przeglądaj wszystkie dostępne kategorie produktów w Kakobuy Spreadsheet. Wybierz interesującą Cię kategorię i odkryj najlepsze oferty.
          </p>
        </div>

        <div className="flex flex-col items-center gap-8">
          <div ref={dropdownRef} className="relative w-full max-w-md animate-fade-in-up animation-delay-100">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 rounded-lg border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 transition-all duration-200 cursor-pointer text-sm"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-grid-line"></i>
                </div>
                <span>{selectedCategory ? selectedCategory.namePl : 'Wybierz kategorię produktów'}</span>
              </div>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-lg shadow-sm z-40 max-h-[320px] overflow-y-auto animate-scale-in">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer text-left border-b border-background-100 last:border-0"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    <span>{cat.namePl}</span>
                    <span className="ml-auto text-xs text-foreground-400">{cat.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-full animate-fade-in-up animation-delay-200">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex flex-col rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
                >
                  <div className="relative h-24 md:h-28 overflow-hidden">
                    <img
                      src={cat.image}
                      alt={cat.namePl}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-900/30 via-transparent to-transparent"></div>
                  </div>
                  <div className="p-3 flex flex-col items-center gap-1">
                    <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-foreground-900 text-center leading-tight">
                      {cat.namePl}
                    </span>
                    <span className="text-[10px] text-foreground-400">{cat.name}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;